import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { useAppDispatch } from '../../hooks/redux';
import { openModal, closeModal } from '../../store/slices/uiSlice';

interface PhoneVerificationProps {
  phoneNumber: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onClose?: () => void;
  modalId?: string;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  onVerify,
  onResend,
  onClose,
  modalId = 'phone-verification',
}) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Open modal and initialize when component mounts
  useEffect(() => {
    dispatch(openModal({ id: modalId }));
    setCode(['', '', '', '', '', '']);
    setError('');
    setCountdown(60);
    // Focus first input after a short delay
    const timer = setTimeout(() => inputRefs.current[0]?.focus(), 100);
    return () => clearTimeout(timer);
  }, [dispatch, modalId]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setCode(digits);
      inputRefs.current[5]?.focus();
      // Auto-verify
      setTimeout(() => handleVerify(pastedData), 100);
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');
    
    if (codeToVerify.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    setError('');
    
    try {
      await onVerify(codeToVerify);
      dispatch(closeModal(modalId));
      onClose?.();
    } catch (error: any) {
      setError(error.message || 'Invalid verification code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    try {
      await onResend();
      setCountdown(60);
    } catch (error: any) {
      setError(error.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = () => {
    dispatch(closeModal(modalId));
    onClose?.();
  };

  return (
    <Modal
      id={modalId}
      title="Verify Phone Number"
      size="sm"
      onClose={handleClose}
    >
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-subtext-color)]">
          We've sent a verification code to <strong>{phoneNumber}</strong>. 
          Please enter the 6-digit code below.
        </p>

        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`
                w-12 h-12 text-center text-lg font-semibold border rounded-md
                bg-[var(--color-default-background)] text-[var(--color-default-font)]
                focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] focus:border-[var(--color-brand-500)]
                ${error ? 'border-[var(--color-error-600)]' : 'border-[var(--color-neutral-border)]'}
              `}
            />
          ))}
        </div>

        {error && (
          <div className="p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-md">
            <p className="text-sm text-[var(--color-error-600)]">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => handleVerify()}
            isLoading={isVerifying}
            disabled={isVerifying || code.some(d => !d)}
            fullWidth
          >
            Verify
          </Button>

          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-sm text-[var(--color-subtext-color)]">
                Resend code in {countdown}s
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-sm text-[var(--color-brand-primary)] hover:underline disabled:opacity-50"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PhoneVerification;

